import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useQuote } from "@/lib/quoteContext";
import LiveStatus from "@/components/site/LiveStatus";

// In-site links: { to, hash, label, type }
const LINKS = [
  { to: "/services", label: "Services", type: "page" },
  { to: "/about", label: "About", type: "page" },
  { to: "/gallery", label: "Gallery", type: "page" },
  { to: "/projects", label: "Projects", type: "page" },
  { to: "/", hash: "#stories", label: "Clients", type: "anchor" },
  { to: "/contact", label: "Contact", type: "page" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openQuote } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When landing on home with a hash, smooth-scroll to it (poll for element).
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const target = location.hash;
      let attempts = 0;
      const tryScroll = () => {
        const el = document.querySelector(target);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempts < 30) {
          attempts += 1;
          setTimeout(tryScroll, 60);
        }
      };
      tryScroll();
    }
  }, [location]);

  const goAnchor = (hash) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/${hash}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.png"
            alt="Emporia World"
            className="w-11 h-11 md:w-12 md:h-12 object-contain transition-transform duration-300 group-hover:rotate-[8deg]"
          />
          <span className="font-heading font-black tracking-tighter text-2xl md:text-[28px] text-ink-900">
            EMPORIA<span className="text-signal">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-5 lg:gap-7">
          {LINKS.map((l) =>
            l.type === "page" ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`font-mono text-[12px] uppercase tracking-[0.22em] hover:text-signal transition-colors ${
                  location.pathname === l.to ? "text-signal" : "text-ink-700"
                }`}
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                type="button"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => goAnchor(l.hash)}
                className="font-mono text-[12px] uppercase tracking-[0.22em] text-ink-700 hover:text-signal transition-colors"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LiveStatus variant="compact" className="hidden xl:inline-flex text-ink-700" />
          <button
            type="button"
            onClick={openQuote}
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-signal text-white font-mono text-[12px] uppercase tracking-[0.2em] hover:bg-signal-600 transition-colors"
          >
            Get a Quote
            <span aria-hidden>→</span>
          </button>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label="Open menu"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-ink-900 text-ink-900"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-black/5" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) =>
              l.type === "page" ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="text-left font-mono text-[13px] uppercase tracking-[0.2em] text-ink-800"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  type="button"
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => goAnchor(l.hash)}
                  className="text-left font-mono text-[13px] uppercase tracking-[0.2em] text-ink-800"
                >
                  {l.label}
                </button>
              )
            )}
            <button
              data-testid="mobile-cta-button"
              onClick={() => { setOpen(false); openQuote(); }}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-signal text-white font-mono text-[12px] uppercase tracking-[0.2em]"
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
